import type { LocalDate } from '../../platform/localDate'
import { writeView, type CashflowView } from './cashflowPeriod'

/**
 * What a drill-down page is scoped to. A payer is an income source with no merchant id; `no_merchant`
 * is every spending row with no merchant id (the "No merchant" row, backend 512).
 */
export type DetailScope =
  | { kind: 'category'; id: string }
  | { kind: 'group'; id: string }
  | { kind: 'merchant'; id: string }
  | { kind: 'payer'; name: string }
  | { kind: 'uncategorised' }
  | { kind: 'no_merchant' }
export type Side = 'out' | 'in'
export type LinkTarget = DetailScope & { side?: Side }

export const CASHFLOW_DETAIL_PATH = '/cashflow/:scope/:id?'

/**
 * A payer name is free text, and React Router cannot hand back a literal `%2F`
 * (it reads `%252F` as `/`). So `%` and `~` are escaped with `~` before the usual
 * encoding; `unescapePayer` reverses it, and every `~` in the output is followed
 * by `7E` or `25`, so it is unambiguous.
 */
const escapePayer = (name: string): string => encodeURIComponent(name.replaceAll('~', '~7E').replaceAll('%', '~25'))
const unescapePayer = (id: string): string => id.replace(/~(7E|25)/g, (_, c: string) => (c === '7E' ? '~' : '%'))

const segment = (t: DetailScope): string => {
  switch (t.kind) {
    case 'uncategorised':
      return '/cashflow/uncategorised'
    case 'no_merchant':
      return '/cashflow/no-merchant'
    case 'payer':
      return `/cashflow/payer/${escapePayer(t.name)}`
    default:
      return `/cashflow/${t.kind}/${encodeURIComponent(t.id)}`
  }
}

/** '/cashflow/category/abc?granularity=quarter&period=2026-01-01&side=in'. Defaults are omitted, as `writeView` does. */
export function detailPath(t: LinkTarget, view: CashflowView, asOf: LocalDate): string {
  const q = writeView(view, asOf)
  if (t.side === 'in') q.set('side', 'in')
  const s = q.toString()
  return s === '' ? segment(t) : `${segment(t)}?${s}`
}

/** null for an unknown scope word, or a missing id where one is needed. */
export function readScope(params: { scope?: string; id?: string }): DetailScope | null {
  const { scope, id } = params
  if (scope === 'uncategorised') return id === undefined ? { kind: 'uncategorised' } : null
  if (scope === 'no-merchant') return id === undefined ? { kind: 'no_merchant' } : null
  if (id === undefined || id === '') return null
  if (scope === 'category' || scope === 'group' || scope === 'merchant') return { kind: scope, id }
  if (scope === 'payer') return { kind: 'payer', name: unescapePayer(id) }
  return null
}

export function readSide(search: URLSearchParams): Side {
  return search.get('side') === 'in' ? 'in' : 'out'
}

/** The query params for both endpoints. */
export function scopeQuery(s: DetailScope): {
  category_id?: string
  group_id?: string
  merchant_id?: string
  merchant_name?: string
  uncategorized?: true
  no_merchant?: true
} {
  switch (s.kind) {
    case 'category':
      return { category_id: s.id }
    case 'group':
      return { group_id: s.id }
    case 'merchant':
      return { merchant_id: s.id }
    case 'payer':
      return { merchant_name: s.name }
    case 'uncategorised':
      return { uncategorized: true }
    case 'no_merchant':
      return { no_merchant: true }
  }
}

/** The report echoes `window.scope.kind` in its own words; this is the ONE table that maps them to ours. */
export const ECHO_KIND = {
  category: 'category',
  group: 'group',
  merchant: 'merchant',
  uncategorised: 'uncategorized',
  payer: 'merchant_name',
  no_merchant: 'no_merchant',
} as const satisfies Record<DetailScope['kind'], string>
export type EchoKind = (typeof ECHO_KIND)[DetailScope['kind']]
export const ECHO_KINDS: readonly EchoKind[] = Object.values(ECHO_KIND)

/** The echo a server owes for this scope: `merchant_name` carries the name as sent, `uncategorized` and `no_merchant` have no id. */
export function expectedEcho(s: DetailScope): { kind: EchoKind; id: string | null } {
  switch (s.kind) {
    case 'uncategorised':
      return { kind: ECHO_KIND.uncategorised, id: null }
    case 'no_merchant':
      return { kind: ECHO_KIND.no_merchant, id: null }
    case 'payer':
      return { kind: ECHO_KIND.payer, id: s.name }
    default:
      return { kind: ECHO_KIND[s.kind], id: s.id }
  }
}
